import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { operatorConsole, FooterLink, FooterLinkType } from '../../lib/api/operatorConsole';

const MAX_LINKS = 10;

// 'other' is the default plain entry; 'terms' / 'privacy' replace the
// matching static anchors inside the iframe (each allowed at most once).
const LINK_TYPES: { value: FooterLinkType; label: string }[] = [
  { value: 'other', label: 'Other' },
  { value: 'terms', label: 'Terms' },
  { value: 'privacy', label: 'Privacy' },
];

// Local row identity: rows are added/removed while editing, so list keys
// need ids that survive reordering (plain indexes would remount inputs).
interface LinkRow extends FooterLink {
  id: number;
}

// Module-level counter: ids only need to be unique within the rendered
// list, so a shared monotonic counter is enough (no per-mount state).
let nextRowId = 1;

function toRows(links: FooterLink[]): LinkRow[] {
  return links.map((link) => ({ ...link, id: nextRowId++ }));
}

export function FooterLinksPage() {
  const [rows, setRows] = useState<LinkRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void operatorConsole
      .getFooterLinks()
      .then((result) => {
        if (alive) setRows(toRows(result));
      })
      .catch((err: unknown) => {
        if (alive) setError(`Failed to load links: ${err instanceof Error ? err.message : err}`);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  function updateRow(id: number, patch: Partial<FooterLink>) {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function addRow() {
    setRows((prev) => [...prev, { id: nextRowId++, label: '', url: '', type: 'other' }]);
  }

  function removeRow(id: number) {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);
      // Fully blank rows are UI leftovers — drop them client-side, the
      // backend does the same normalization.
      const payload = rows
        .filter((row) => row.label.trim() !== '' || row.url.trim() !== '')
        .map(({ label, url, type }) => ({ label, url, type }));
      const saved = await operatorConsole.putFooterLinks(payload);
      setRows(toRows(saved));
      setSuccess('Links saved');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      setError(`Save failed: ${err instanceof Error ? err.message : err}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6">
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold text-[var(--co-text)]">Links</h1>
          <p className="text-[12px] text-[var(--co-muted)]">
            Partner links shown in the iframe footer (My Packs / Delivery). With no links
            configured the iframe hides the footer.
          </p>
        </div>
        {(error || success) && (
          <span
            className={`rounded px-2 py-1 text-[11px] font-medium ${
              error ? 'bg-[var(--co-error)] text-white' : 'bg-[var(--co-success)] text-white'
            }`}
          >
            {error ?? success}
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-[13px] text-[var(--co-muted)]">Loading links…</p>
      ) : (
        <div className="max-w-2xl rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4">
          {rows.length === 0 && (
            <p className="mb-3 text-[13px] text-[var(--co-muted)]">
              No links yet — add the first one.
            </p>
          )}

          {rows.map((row, index) => (
            <div key={row.id} className="mb-2 flex items-center gap-2">
              <input
                type="text"
                value={row.label}
                onChange={(e) => updateRow(row.id, { label: e.target.value })}
                placeholder="FAQ"
                maxLength={64}
                aria-label={`Link ${index + 1} label`}
                className="w-40 rounded-lg border border-[var(--co-border)] bg-transparent px-3 py-2 text-[13px] text-[var(--co-text)] outline-none focus:border-[var(--co-brand)]"
              />
              <input
                type="url"
                value={row.url}
                onChange={(e) => updateRow(row.id, { url: e.target.value })}
                placeholder="https://partner.com/faq"
                aria-label={`Link ${index + 1} URL`}
                className="flex-1 rounded-lg border border-[var(--co-border)] bg-transparent px-3 py-2 text-[13px] text-[var(--co-text)] outline-none focus:border-[var(--co-brand)]"
              />
              <select
                value={row.type}
                onChange={(e) => updateRow(row.id, { type: e.target.value as FooterLinkType })}
                aria-label={`Link ${index + 1} type`}
                className="w-28 rounded-lg border border-[var(--co-border)] bg-[var(--co-surface)] px-2 py-2 text-[13px] text-[var(--co-text)] outline-none focus:border-[var(--co-brand)]"
              >
                {LINK_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeRow(row.id)}
                title="Remove link"
                aria-label={`Remove link ${index + 1}`}
                className="rounded-lg p-2 text-[var(--co-muted)] hover:bg-[var(--co-error)]/10 hover:text-[var(--co-error)]"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}

          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={addRow}
              disabled={rows.length >= MAX_LINKS}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--co-border)] px-3 py-2 text-[12px] font-medium text-[var(--co-text)] hover:bg-[var(--co-border)]/30 disabled:opacity-50"
            >
              <Plus size={14} />
              Add link
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="rounded-lg bg-[var(--co-brand)] px-4 py-2 text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {rows.length >= MAX_LINKS && (
              <span className="text-[11px] text-[var(--co-muted)]">
                Maximum {MAX_LINKS} links
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
